document.body.style.overflowY = "hidden"
import {
    ViewerApp,
    AssetManagerPlugin,
    timeout,
    SSRPlugin,
    mobileAndTabletCheck,
    GBufferPlugin,
    ProgressivePlugin,
    TonemapPlugin,
    SSAOPlugin,
    FrameFadePlugin,
    DiamondPlugin,
    BufferGeometry,
    MeshStandardMaterial2,
    BloomPlugin,
    TemporalAAPlugin,
    RandomizedDirectionalLightPlugin,
    AssetImporter,
    Color,
    Mesh
} from "webgi"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import Lenis from '@studio-freight/lenis'

import "./styles.scss"
import sceneUrl from '../assets/scene.glb'
import { generateRingImage } from './generator/services/gemini'
import { RingConfig, ImageSize } from './generator/types'

// ============================================================================
// PERSONALITY SCORING ENGINE
// ============================================================================

interface SliderValues {
    // Step 1: Personality traits (left = -1, right = +1)
    'expressive-reserved': number      // expressive (-1) <-> reserved (+1)
    'realistic-visionary': number      // realistic (-1) <-> visionary (+1)
    'analytical-empathetic': number    // analytical (-1) <-> empathetic (+1)
    'planned-flexible': number         // planned (-1) <-> flexible (+1)
    'quiet-loud': number               // quiet (-1) <-> loud (+1)
    'structured-spontaneous': number   // structured (-1) <-> spontaneous (+1)
    'risktaker-riskaverse': number     // risktaker (-1) <-> riskaverse (+1)
    // Step 2: Style preferences
    'subtle-bold': number              // subtle (-1) <-> bold (+1)
    'minimal-expressive': number       // minimal (-1) <-> expressive (+1)
    'timeless-inventive': number       // timeless (-1) <-> inventive (+1)
    'traditional-artistic': number     // traditional (-1) <-> artistic (+1)
    'structured-organic': number       // structured (-1) <-> organic (+1)
    'sharp-soft': number               // sharp (-1) <-> soft (+1)
}

interface PersonalityScores {
    expression: number  // -1 (subtle) to +1 (bold)
    structure: number   // -1 (structured) to +1 (organic)
    shape: number       // -1 (sharp) to +1 (soft)
    creativity: number  // -1 (classic) to +1 (inventive)
    risk: number        // -1 (risk taker) to +1 (risk averse)
}

interface RingRecommendations {
    material: 'gold' | 'silver'
    materialIndex: number
    stoneCuts: string[]
    stoneType: string
    stoneIndex: number
    bandStyle: string
    bandIndex: number
    setting: string
    settingIndex: number
}

// Storage for slider values and computed scores
const sliderValues: SliderValues = {
    'expressive-reserved': 0,
    'realistic-visionary': 0,
    'analytical-empathetic': 0,
    'planned-flexible': 0,
    'quiet-loud': 0,
    'structured-spontaneous': 0,
    'risktaker-riskaverse': 0,
    'subtle-bold': 0,
    'minimal-expressive': 0,
    'timeless-inventive': 0,
    'traditional-artistic': 0,
    'structured-organic': 0,
    'sharp-soft': 0
}

let personalityScores: PersonalityScores = {
    expression: 0,
    structure: 0,
    shape: 0,
    creativity: 0,
    risk: 0
}

let recommendations: RingRecommendations = {
    material: 'gold',
    materialIndex: 0,
    stoneCuts: ['round', 'cushion', 'oval'],
    stoneType: 'diamond',
    stoneIndex: 0,
    bandStyle: 'Plain',
    bandIndex: 0,
    setting: 'Solitaire',
    settingIndex: 0
}

// Compute 5 core personality scores from raw slider values
function computePersonalityScores(): PersonalityScores {
    // EXPRESSION SCORE: combines expressive/reserved, quiet/loud, subtle/bold, minimal/expressive
    // Note: expressive-reserved: expressive is left (-1), reserved is right (+1)
    // quiet-loud: quiet is left (-1), loud is right (+1)
    // subtle-bold: subtle is left (-1), bold is right (+1)
    // minimal-expressive: minimal is left (-1), expressive is right (+1)
    const expression = (
        -sliderValues['expressive-reserved'] +  // flip: expressive should be positive
        sliderValues['quiet-loud'] +            // loud is positive
        sliderValues['subtle-bold'] +           // bold is positive
        sliderValues['minimal-expressive']      // expressive is positive
    ) / 4

    // STRUCTURE SCORE: combines planned/flexible, structured/spontaneous, structured/organic
    // planned-flexible: planned is left (-1), flexible is right (+1)
    // structured-spontaneous: structured is left (-1), spontaneous is right (+1)
    // structured-organic: structured is left (-1), organic is right (+1)
    const structure = (
        sliderValues['planned-flexible'] +
        sliderValues['structured-spontaneous'] +
        sliderValues['structured-organic']
    ) / 3

    // SHAPE SCORE: combines analytical/empathetic, sharp/soft
    // analytical-empathetic: analytical is left (-1), empathetic is right (+1)
    // sharp-soft: sharp is left (-1), soft is right (+1)
    const shape = (
        sliderValues['analytical-empathetic'] +
        sliderValues['sharp-soft']
    ) / 2

    // CREATIVITY SCORE: combines realistic/visionary, timeless/inventive, traditional/artistic
    // realistic-visionary: realistic is left (-1), visionary is right (+1)
    // timeless-inventive: timeless is left (-1), inventive is right (+1)
    // traditional-artistic: traditional is left (-1), artistic is right (+1)
    const creativity = (
        sliderValues['realistic-visionary'] +
        sliderValues['timeless-inventive'] +
        sliderValues['traditional-artistic']
    ) / 3

    // RISK SCORE: risktaker/riskaverse
    // risktaker-riskaverse: risktaker is left (-1), riskaverse is right (+1)
    // We flip this so risk-taking is positive
    const risk = -sliderValues['risktaker-riskaverse']

    return { expression, structure, shape, creativity, risk }
}

// Band styles list for reference
const allBandStyles = ['Plain', 'Split Shank', 'Bypass', 'Euro Shank', 'Tapered', 'Twisted', 'Trefoil', 'Checker', 'Checkerboard', 'Coiled', 'Studded', 'Lattice Pave', 'Spiral', 'Smooth Fluted', 'Corrugated', 'Open Cuff', 'Signet', 'Contrast Asymmetry']

// Setting styles list
const allSettings = ['Solitaire', 'Double Four Prongs', 'Three Stone', 'Pave', 'Bezel', 'Crown', 'Signet', 'Chunky Oval', '4 Prongs', 'Pave Marquise', 'Pseudo Claws', 'Lozenge Pave', 'Plain Marquise']

// Stone types list (matching carousel order)
const allStones = ['diamond', 'amethyst', 'aquamarine', 'ruby', 'emerald', 'citrine', 'sapphire', 'ametrine', 'tanzanite']

// Generate ring recommendations based on personality scores
function generateRecommendations(scores: PersonalityScores): RingRecommendations {
    const { expression, structure, shape, creativity, risk } = scores

    // MATERIAL: based on shape and expression
    // Empathetic/soft/expressive/warm -> gold
    // Analytical/sharp/reserved/cool -> silver
    let material: 'gold' | 'silver' = 'gold'
    let materialIndex = 0
    if (shape < -0.2 && expression < 0.2) {
        material = 'silver'
        materialIndex = 1
    }

    // STONE CUT: based on shape score
    let stoneCuts: string[] = []
    if (shape < -0.3) {
        // Sharp cuts for analytical
        stoneCuts = ['princess', 'emerald', 'baguette']
    } else if (shape > 0.3) {
        // Soft cuts for empathetic
        stoneCuts = ['oval', 'cushion', 'round']
    } else {
        // Balanced
        stoneCuts = ['radiant', 'cushion', 'round']
    }

    // Add creative/risk cuts
    if (creativity > 0.5 || risk > 0.5) {
        stoneCuts = ['pear', 'marquise', 'trillion', ...stoneCuts]
    }

    // STONE TYPE: based on creativity score
    let stoneType: string
    let stoneIndex: number
    if (creativity < -0.3) {
        // Classic stones
        const classicStones = [{ name: 'ruby', idx: 3 }, { name: 'sapphire', idx: 6 }, { name: 'emerald', idx: 4 }]
        const pick = classicStones[Math.floor(Math.abs(expression * 3) % 3)]
        stoneType = pick.name
        stoneIndex = pick.idx
    } else if (creativity > 0.5) {
        // Creative/unique stones
        const creativeStones = [{ name: 'tanzanite', idx: 8 }, { name: 'ametrine', idx: 7 }]
        const pick = creativeStones[Math.floor(Math.abs(structure * 2) % 2)]
        stoneType = pick.name
        stoneIndex = pick.idx
    } else {
        // Middle ground
        const midStones = [{ name: 'amethyst', idx: 1 }, { name: 'aquamarine', idx: 2 }, { name: 'citrine', idx: 5 }, { name: 'diamond', idx: 0 }]
        const pick = midStones[Math.floor(Math.abs(shape * 4) % 4)]
        stoneType = pick.name
        stoneIndex = pick.idx
    }

    // BAND STYLE: based on structure and expression scores
    let bandStyle: string
    let bandIndex: number

    if (structure < -0.3) {
        // Structured -> straight, symmetric, signet
        const structuredBands = [{ name: 'Plain', idx: 0 }, { name: 'Signet', idx: 16 }, { name: 'Euro Shank', idx: 3 }]
        const pick = structuredBands[Math.floor(Math.abs(expression * 3) % 3)]
        bandStyle = pick.name
        bandIndex = pick.idx
    } else if (structure > 0.3) {
        // Organic -> twisted, fluid, open
        const organicBands = [{ name: 'Twisted', idx: 5 }, { name: 'Spiral', idx: 12 }, { name: 'Open Cuff', idx: 15 }, { name: 'Coiled', idx: 9 }]
        const pick = organicBands[Math.floor(Math.abs(expression * 4) % 4)]
        bandStyle = pick.name
        bandIndex = pick.idx
    } else {
        // Balanced
        const balancedBands = [{ name: 'Tapered', idx: 4 }, { name: 'Split Shank', idx: 1 }, { name: 'Smooth Fluted', idx: 13 }]
        const pick = balancedBands[Math.floor(Math.abs(creativity * 3) % 3)]
        bandStyle = pick.name
        bandIndex = pick.idx
    }

    // Adjust for high expression
    if (expression > 0.5) {
        // Bold expression -> thicker, layered bands
        const boldBands = [{ name: 'Checkerboard', idx: 8 }, { name: 'Lattice Pave', idx: 11 }, { name: 'Studded', idx: 10 }]
        const pick = boldBands[Math.floor(Math.abs(structure * 3) % 3)]
        bandStyle = pick.name
        bandIndex = pick.idx
    }

    // SETTING: based on expression and structure
    let setting: string
    let settingIndex: number

    if (expression > 0.3 && structure > 0.3) {
        // Bold + organic -> statement settings
        const statementSettings = [{ name: 'Chunky Oval', idx: 7 }, { name: 'Pave Marquise', idx: 9 }, { name: 'Crown', idx: 5 }]
        const pick = statementSettings[Math.floor(Math.abs(creativity * 3) % 3)]
        setting = pick.name
        settingIndex = pick.idx
    } else if (expression < -0.3) {
        // Subtle -> minimal settings
        const minimalSettings = [{ name: 'Solitaire', idx: 0 }, { name: '4 Prongs', idx: 8 }, { name: 'Bezel', idx: 4 }]
        const pick = minimalSettings[Math.floor(Math.abs(shape * 3) % 3)]
        setting = pick.name
        settingIndex = pick.idx
    } else {
        // Balanced
        const balancedSettings = [{ name: 'Three Stone', idx: 2 }, { name: 'Double Four Prongs', idx: 1 }, { name: 'Pave', idx: 3 }]
        const pick = balancedSettings[Math.floor(Math.abs(risk * 3) % 3)]
        setting = pick.name
        settingIndex = pick.idx
    }

    return {
        material,
        materialIndex,
        stoneCuts,
        stoneType,
        stoneIndex,
        bandStyle,
        bandIndex,
        setting,
        settingIndex
    }
}

// Get description text based on recommendations
function getRecommendationDescription(type: 'material' | 'setting' | 'band' | 'stone' | 'cut'): string {
    const { expression, structure, shape, creativity } = personalityScores

    if (type === 'material') {
        if (recommendations.material === 'gold') {
            return 'Your warm, expressive personality pairs beautifully with gold\'s rich, classic warmth.'
        } else {
            return 'Your analytical, modern aesthetic aligns with silver\'s cool, sophisticated elegance.'
        }
    } else if (type === 'setting') {
        if (expression > 0.3) {
            return 'Your bold personality calls for a statement setting that commands attention.'
        } else if (expression < -0.3) {
            return 'Your subtle elegance is best expressed through a clean, minimal setting.'
        } else {
            return 'Your balanced style pairs well with this versatile, timeless setting.'
        }
    } else if (type === 'band') {
        if (structure > 0.3) {
            return 'Your fluid, spontaneous nature is reflected in this organic, flowing band design.'
        } else if (structure < -0.3) {
            return 'Your structured approach is mirrored in this clean, geometric band style.'
        } else {
            return 'This band style complements your balanced aesthetic perfectly.'
        }
    } else if (type === 'stone') {
        if (creativity > 0.5) {
            return 'Your creative, inventive spirit is matched by this unique and expressive stone.'
        } else if (creativity < -0.3) {
            return 'Your timeless taste is reflected in this classic, elegant gemstone.'
        } else {
            return 'This stone captures your balanced, versatile personality beautifully.'
        }
    } else {
        // cut
        if (shape > 0.3) {
            return 'Your soft, empathetic nature is expressed through this gentle, rounded cut.'
        } else if (shape < -0.3) {
            return 'Your analytical precision is mirrored in this sharp, geometric cut.'
        } else {
            return 'This cut strikes the perfect balance between classic and contemporary.'
        }
    }
}

// Import cut images for each stone
import amethystAscher from '../assets/images/amethyst/ascher.png'
import amethystBaguette from '../assets/images/amethyst/baguette.png'
import amethystCushion from '../assets/images/amethyst/cushion.png'
import amethystEmerald from '../assets/images/amethyst/emerald.png'
import amethystMarquise from '../assets/images/amethyst/marquise.png'
import amethystPear from '../assets/images/amethyst/pear.png'
import amethystPrincess from '../assets/images/amethyst/princess.png'
import amethystRound from '../assets/images/amethyst/round.png'
import amethystTrillion from '../assets/images/amethyst/trillion.png'

import aquamarineAscher from '../assets/images/aquamarine/ascher.png'
import aquamarineBaguette from '../assets/images/aquamarine/baguette.png'
import aquamarineCushion from '../assets/images/aquamarine/cushion.png'
import aquamarineEmerald from '../assets/images/aquamarine/emerald.png'
import aquamarineMarquise from '../assets/images/aquamarine/marquise.png'
import aquamarineOval from '../assets/images/aquamarine/oval.png'
import aquamarinePear from '../assets/images/aquamarine/pear.png'
import aquamarinePrincess from '../assets/images/aquamarine/princess.png'
import aquamarineRound from '../assets/images/aquamarine/round.png'
import aquamarineTrillion from '../assets/images/aquamarine/trillion.png'

import citrineAscher from '../assets/images/citrine/ascher.png'
import citrineBaguette from '../assets/images/citrine/baguette.png'
import citrineCushion from '../assets/images/citrine/cushion.png'
import citrineEmerald from '../assets/images/citrine/emerald.png'
import citrineMarquise from '../assets/images/citrine/marquise.png'
import citrineOval from '../assets/images/citrine/oval.png'
import citrinePear from '../assets/images/citrine/pear.png'
import citrinePrincess from '../assets/images/citrine/princess.png'
import citrineRound from '../assets/images/citrine/round.png'
import citrineTrillion from '../assets/images/citrine/trillion.png'

import emeraldAscher from '../assets/images/emerald/ascher.png'
import emeraldBaguette from '../assets/images/emerald/baguette.png'
import emeraldCushion from '../assets/images/emerald/cushion.png'
import emeraldEmerald from '../assets/images/emerald/emerald.png'
import emeraldMarquise from '../assets/images/emerald/marquise.png'
import emeraldOval from '../assets/images/emerald/oval.png'
import emeraldPear from '../assets/images/emerald/pear.png'
import emeraldPrincess from '../assets/images/emerald/princess.png'
import emeraldRound from '../assets/images/emerald/round.png'
import emeraldTrillion from '../assets/images/emerald/trillion.png'

import ametrineAscher from '../assets/images/l_amertine/ascher.png'
import ametrineBaguette from '../assets/images/l_amertine/baguette.png'
import ametrineCushion from '../assets/images/l_amertine/cushion.png'
import ametrineEmerald from '../assets/images/l_amertine/emerald.png'
import ametrineMarquise from '../assets/images/l_amertine/marquise.png'
import ametrineOval from '../assets/images/l_amertine/oval.png'
import ametrinePear from '../assets/images/l_amertine/pear.png'
import ametrinePrincess from '../assets/images/l_amertine/princess.png'
import ametrineRound from '../assets/images/l_amertine/round.png'
import ametrineTrillion from '../assets/images/l_amertine/trillion.png'

import rubyAscher from '../assets/images/ruby/ascher.png'
import rubyBaguette from '../assets/images/ruby/baguette.png'
import rubyCushion from '../assets/images/ruby/cushion.png'
import rubyEmerald from '../assets/images/ruby/emerald.png'
import rubyMarquise from '../assets/images/ruby/marquise.png'
import rubyOval from '../assets/images/ruby/oval.png'
import rubyPear from '../assets/images/ruby/pear.png'
import rubyPrincess from '../assets/images/ruby/princess.png'
import rubyRound from '../assets/images/ruby/round.png'
import rubyTrillion from '../assets/images/ruby/trillion.png'

import sapphireAscher from '../assets/images/sapphire/ascher.png'
import sapphireBaguette from '../assets/images/sapphire/baguette.png'
import sapphireCushion from '../assets/images/sapphire/cushion.png'
import sapphireEmerald from '../assets/images/sapphire/emerald.png'
import sapphireMarquise from '../assets/images/sapphire/marquise.png'
import sapphireOval from '../assets/images/sapphire/oval.png'
import sapphirePear from '../assets/images/sapphire/pear.png'
import sapphirePrincess from '../assets/images/sapphire/princess.png'
import sapphireRound from '../assets/images/sapphire/round.png'
import sapphireTrillion from '../assets/images/sapphire/trillion.png'

import tanzaniteAscher from '../assets/images/tanzanite bicolor/ascher.png'
import tanzaniteBaguette from '../assets/images/tanzanite bicolor/baguette.png'
import tanzaniteCushion from '../assets/images/tanzanite bicolor/cushion.png'
import tanzaniteEmerald from '../assets/images/tanzanite bicolor/emerald.png'
import tanzaniteMarquise from '../assets/images/tanzanite bicolor/marquise.png'
import tanzaniteOval from '../assets/images/tanzanite bicolor/oval.png'
import tanzanitePear from '../assets/images/tanzanite bicolor/pear.png'
import tanzanitePrincess from '../assets/images/tanzanite bicolor/princess.png'
import tanzaniteRound from '../assets/images/tanzanite bicolor/round.png'
import tanzaniteTrillion from '../assets/images/tanzanite bicolor/trillion.png'

import diamondAscher from '../assets/images/diamond/ascher.png'
import diamondBaguette from '../assets/images/diamond/baguette.png'
import diamondCushion from '../assets/images/diamond/cushion.png'
import diamondEmerald from '../assets/images/diamond/emerald.png'
import diamondMarquise from '../assets/images/diamond/marquise.png'
import diamondOval from '../assets/images/diamond/oval.png'
import diamondPear from '../assets/images/diamond/pear.png'
import diamondPrincess from '../assets/images/diamond/princess.png'
import diamondRound from '../assets/images/diamond/round.png'
import diamondTrillion from '../assets/images/diamond/trillion.png'

// Import gold band images
import goldPlain from '../assets/images/rings/gold bands/plain.png'
import goldSplitShank from '../assets/images/rings/gold bands/split shank.png'
import goldBypass from '../assets/images/rings/gold bands/bypass.png'
import goldEuroShank from '../assets/images/rings/gold bands/euro shank.png'
import goldTapered from '../assets/images/rings/gold bands/tapered.png'
import goldTwisted from '../assets/images/rings/gold bands/twisted.png'
import goldTrefoil from '../assets/images/rings/gold bands/trefoil.png'
import goldChecker from '../assets/images/rings/gold bands/checker.png'
import goldCheckerboard from '../assets/images/rings/gold bands/checker board.png'
import goldCoiled from '../assets/images/rings/gold bands/coiled.png'
import goldStudded from '../assets/images/rings/gold bands/studded.png'
import goldLatticePave from '../assets/images/rings/gold bands/lattice pave.png'
import goldSpiral from '../assets/images/rings/gold bands/spiral.png'
import goldSmoothFluted from '../assets/images/rings/gold bands/smooth fluted.png'
import goldCorrugated from '../assets/images/rings/gold bands/corrugated.png'
import goldOpenCuff from '../assets/images/rings/gold bands/open cuff.png'
import goldSignet from '../assets/images/rings/gold bands/signet.png'
import goldContrastAsymmetry from '../assets/images/rings/gold bands/contrast asymmetry.png'

// Import silver band images
import silverPlain from '../assets/images/rings/silver bands/plain.png'
import silverSplitShank from '../assets/images/rings/silver bands/split shank.png'
import silverBypass from '../assets/images/rings/silver bands/bypass.png'
import silverEuroShank from '../assets/images/rings/silver bands/euro shank.png'
import silverTapered from '../assets/images/rings/silver bands/tapered.png'
import silverTwisted from '../assets/images/rings/silver bands/twisted.png'
import silverTrefoil from '../assets/images/rings/silver bands/trefoil.png'
import silverChecker from '../assets/images/rings/silver bands/checker.png'
import silverCheckerboard from '../assets/images/rings/silver bands/checker board.png'
import silverCoiled from '../assets/images/rings/silver bands/coiled.png'
import silverStudded from '../assets/images/rings/silver bands/studded.png'
import silverLatticePave from '../assets/images/rings/silver bands/lattice pave.png'
import silverSpiral from '../assets/images/rings/silver bands/spiral.png'
import silverSmoothFluted from '../assets/images/rings/silver bands/smooth fluted.png'
import silverCorrugated from '../assets/images/rings/silver bands/corrugated.png'
import silverOpenCuff from '../assets/images/rings/silver bands/open cuff.png'
import silverSignet from '../assets/images/rings/silver bands/signet.png'
import silverContrastAsymmetry from '../assets/images/rings/silver bands/contrast asymmetry.png'

// Band image mapping
const bandImages: { [metal: string]: { [band: string]: string } } = {
    gold: {
        'Plain': goldPlain, 'Split Shank': goldSplitShank, 'Bypass': goldBypass,
        'Euro Shank': goldEuroShank, 'Tapered': goldTapered, 'Twisted': goldTwisted,
        'Trefoil': goldTrefoil, 'Checker': goldChecker, 'Checkerboard': goldCheckerboard,
        'Coiled': goldCoiled, 'Studded': goldStudded, 'Lattice Pave': goldLatticePave,
        'Spiral': goldSpiral, 'Smooth Fluted': goldSmoothFluted, 'Corrugated': goldCorrugated,
        'Open Cuff': goldOpenCuff, 'Signet': goldSignet, 'Contrast Asymmetry': goldContrastAsymmetry
    },
    silver: {
        'Plain': silverPlain, 'Split Shank': silverSplitShank, 'Bypass': silverBypass,
        'Euro Shank': silverEuroShank, 'Tapered': silverTapered, 'Twisted': silverTwisted,
        'Trefoil': silverTrefoil, 'Checker': silverChecker, 'Checkerboard': silverCheckerboard,
        'Coiled': silverCoiled, 'Studded': silverStudded, 'Lattice Pave': silverLatticePave,
        'Spiral': silverSpiral, 'Smooth Fluted': silverSmoothFluted, 'Corrugated': silverCorrugated,
        'Open Cuff': silverOpenCuff, 'Signet': silverSignet, 'Contrast Asymmetry': silverContrastAsymmetry
    }
}

// Cut image mapping
const cutImages: { [stone: string]: { [cut: string]: string } } = {
    amethyst: {
        ascher: amethystAscher, baguette: amethystBaguette, cushion: amethystCushion,
        emerald: amethystEmerald, marquise: amethystMarquise, pear: amethystPear,
        princess: amethystPrincess, round: amethystRound, trillion: amethystTrillion
    },
    aquamarine: {
        ascher: aquamarineAscher, baguette: aquamarineBaguette, cushion: aquamarineCushion,
        emerald: aquamarineEmerald, marquise: aquamarineMarquise, oval: aquamarineOval,
        pear: aquamarinePear, princess: aquamarinePrincess, round: aquamarineRound, trillion: aquamarineTrillion
    },
    citrine: {
        ascher: citrineAscher, baguette: citrineBaguette, cushion: citrineCushion,
        emerald: citrineEmerald, marquise: citrineMarquise, oval: citrineOval,
        pear: citrinePear, princess: citrinePrincess, round: citrineRound, trillion: citrineTrillion
    },
    emerald: {
        ascher: emeraldAscher, baguette: emeraldBaguette, cushion: emeraldCushion,
        emerald: emeraldEmerald, marquise: emeraldMarquise, oval: emeraldOval,
        pear: emeraldPear, princess: emeraldPrincess, round: emeraldRound, trillion: emeraldTrillion
    },
    ametrine: {
        ascher: ametrineAscher, baguette: ametrineBaguette, cushion: ametrineCushion,
        emerald: ametrineEmerald, marquise: ametrineMarquise, oval: ametrineOval,
        pear: ametrinePear, princess: ametrinePrincess, round: ametrineRound, trillion: ametrineTrillion
    },
    ruby: {
        ascher: rubyAscher, baguette: rubyBaguette, cushion: rubyCushion,
        emerald: rubyEmerald, marquise: rubyMarquise, oval: rubyOval,
        pear: rubyPear, princess: rubyPrincess, round: rubyRound, trillion: rubyTrillion
    },
    sapphire: {
        ascher: sapphireAscher, baguette: sapphireBaguette, cushion: sapphireCushion,
        emerald: sapphireEmerald, marquise: sapphireMarquise, oval: sapphireOval,
        pear: sapphirePear, princess: sapphirePrincess, round: sapphireRound, trillion: sapphireTrillion
    },
    tanzanite: {
        ascher: tanzaniteAscher, baguette: tanzaniteBaguette, cushion: tanzaniteCushion,
        emerald: tanzaniteEmerald, marquise: tanzaniteMarquise, oval: tanzaniteOval,
        pear: tanzanitePear, princess: tanzanitePrincess, round: tanzaniteRound, trillion: tanzaniteTrillion
    },
    diamond: {
        ascher: diamondAscher, baguette: diamondBaguette, cushion: diamondCushion,
        emerald: diamondEmerald, marquise: diamondMarquise, oval: diamondOval,
        pear: diamondPear, princess: diamondPrincess, round: diamondRound, trillion: diamondTrillion
    }
}

gsap.registerPlugin(ScrollTrigger)

let lenis: Lenis | null = null

function initLenis() {
    if (lenis) return
    lenis = new Lenis({
        duration: 1.2,
        easing: t => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        direction: 'vertical',
        gestureDirection: 'vertical',
        smoothWheel: true,
        wheelMultiplier: 1,
    })

    // Sync Lenis with GSAP ScrollTrigger
    lenis.on('scroll', ScrollTrigger.update)

    gsap.ticker.add((time) => {
        lenis!.raf(time * 1000)
    })

    gsap.ticker.lagSmoothing(0)
}

// Helper: collect meshes from a subtree whose material matches a predicate.
function collectMeshesByMaterial(
    root: any,
    predicate: (materialName: string) => boolean
): any[] {
    const results: any[] = []
    if (!root) return results
    root.traverse((node: any) => {
        if (!node.isMesh) return
        const mats = Array.isArray(node.material) ? node.material : [node.material]
        for (const m of mats) {
            const name = (m?.name || '').toLowerCase()
            if (predicate(name)) {
                results.push(node)
                return
            }
        }
    })
    return results
}

async function setupViewer() {

    const canvas = document.getElementById('webgi-canvas') as HTMLCanvasElement
    const viewer = new ViewerApp({
        canvas,
        useGBufferDepth: true,
        isAntialiased: false
    })

    const isMobile = mobileAndTabletCheck()

    viewer.renderer.displayCanvasScaling = Math.min(window.devicePixelRatio, 1)

    const manager = await viewer.addPlugin(AssetManagerPlugin)
    const camera = viewer.scene.activeCamera
    const position = camera.position
    const target = camera.target

    // Interface Elements
    const loaderElement = document.querySelector('.loader') as HTMLElement
    let firstLooad = true

    // Add WEBGi plugins
    await viewer.addPlugin(GBufferPlugin)
    await viewer.addPlugin(new ProgressivePlugin(32))
    await viewer.addPlugin(new TonemapPlugin(true, false))
    const ssr = await viewer.addPlugin(SSRPlugin)
    const ssao = await viewer.addPlugin(SSAOPlugin)
    await viewer.addPlugin(FrameFadePlugin)
    const bloom = await viewer.addPlugin(BloomPlugin)
    await viewer.addPlugin(TemporalAAPlugin,)
    await viewer.addPlugin(DiamondPlugin)
    await viewer.addPlugin(RandomizedDirectionalLightPlugin, false)

    // Background color matching gradient
    viewer.setBackground(new Color('#AFDAE6').convertSRGBToLinear())

    ssr!.passes.ssr.passObject.lowQualityFrames = 0
    bloom.pass!.passObject.bloomIterations = 2
    ssao.passes.ssao.passObject.material.defines.NUM_SAMPLES = 4

    // WEBGi loader
    const importer = manager.importer as AssetImporter

    importer.addEventListener("onProgress", (ev) => {
        const progressRatio = (ev.loaded / ev.total)
        document.querySelector('.progress')?.setAttribute('style', `transform: scaleX(${progressRatio})`)
    })

    importer.addEventListener("onLoad", (ev) => {
        if (firstLooad) {
            introAnimation()
        } else {
            gsap.to('.loader', { x: '100%', duration: 0.8, ease: "power4.inOut", delay: 1 })
        }
    })

    viewer.renderer.refreshPipeline()

    // WEBGi load model
    await manager.addFromPath(sceneUrl)

    let ring: any, gold: Mesh<BufferGeometry, MeshStandardMaterial2>, silver: Mesh<BufferGeometry, MeshStandardMaterial2>
    let diamondObjects: any[] = []

    // Resolve the ring hierarchy from the GLB
    function findRingRoot(): any {
        const candidates: any[] = []
        const modelRoot: any = (viewer.scene as any).modelRoot || viewer.scene
        modelRoot.traverse((n: any) => {
            if (n === modelRoot) return
            if (n.isMesh) candidates.push(n)
        })
        if (candidates.length === 0) return null
        let node = candidates[0]
        while (node.parent && node.parent !== modelRoot && node.parent.type !== 'Scene') {
            node = node.parent
        }
        return node
    }

    ring = findRingRoot()

    // Find gold and diamond meshes by material name
    const goldMeshes = collectMeshesByMaterial(ring, n => n.includes('gold'))
    const diamondMeshes = collectMeshesByMaterial(ring, n => n.includes('diamond'))

    gold = (goldMeshes[0] || ring.children[0]) as any
    silver = gold

    diamondObjects = diamondMeshes

    // Cached colors to avoid allocations in scroll callbacks
    const cachedGoldStart = new Color(0xe2bf7f).convertSRGBToLinear()
    const cachedWhite = new Color(0xffffff).convertSRGBToLinear()
    const cachedRed = new Color(0xff0000).convertSRGBToLinear()
    const cachedAqua = new Color(0x62cffe).convertSRGBToLinear()
    const cachedViolet = new Color(0xc77dff).convertSRGBToLinear()

    // Set initial colors to match scroll position 0 (prevents flash on scroll start)
    gold.material.color.copy(cachedGoldStart)
    silver.material.color.copy(cachedGoldStart)
    for (const diamond of diamondObjects) {
        if (diamond.material && diamond.material.color) {
            diamond.material.color.copy(cachedRed)
        }
    }

    // Initial ring orientation
    ring.rotation.set(-Math.PI / 3 + Math.PI / 4, Math.PI + Math.PI / 4, Math.PI / 6)
    ring.scale.setScalar(0.75)

    if (camera.controls) {
        camera.controls!.enabled = false
    }

    // WEBGi mobile adjustments
    if (isMobile) {
        ssr.passes.ssr.passObject.stepCount /= 2
        bloom.enabled = false
        camera.setCameraOptions({ fov: 65 })
    }

    window.scrollTo(0, 0)

    await timeout(50)

    function introAnimation() {
        firstLooad = false
        const introTL = gsap.timeline()
        introTL
            .to('.loader', { x: '100%', duration: 0.8, ease: "power4.inOut", delay: 1 })
            .fromTo(position, { x: isMobile ? 3 : 3, y: isMobile ? -0.8 : -0.8, z: isMobile ? 1.2 : 1.2 }, { x: isMobile ? 1.28 : 1.28, y: isMobile ? -1.7 : -1.7, z: isMobile ? 5.86 : 5.86, duration: 4, onUpdate }, '-=0.8')
            .fromTo(target, { x: isMobile ? 2.5 : 2.5, y: isMobile ? -0.07 : -0.07, z: isMobile ? -0.1 : -0.1 }, { x: isMobile ? -0.21 : 0.91, y: isMobile ? 0.03 : 0.03, z: isMobile ? -0.25 : -0.25, duration: 4, onUpdate }, '-=4')
            .fromTo('.header--container', { opacity: 0, y: '-100%' }, { opacity: 1, y: '0%', ease: "power1.inOut", duration: 0.8 }, '-=1')
            .fromTo('.hero--container', { opacity: 0 }, { opacity: 1, ease: "power4.inOut", duration: 1.8, onComplete: setupScrollAnimation }, '-=1')
            .fromTo('.scroll-prompt', { opacity: 0 }, { opacity: 1, ease: "power4.inOut", duration: 1.2 }, '-=0.8')
            .fromTo('.side-bar', { opacity: 0, x: '50%' }, { opacity: 1, x: '0%', ease: "power4.inOut", duration: 2 }, '-=1')
            .to('.side-bar .unique', { opacity: 1, scale: 1.5, ease: "power4.inOut", duration: 2 }, '-=1')
    }

    function setupScrollAnimation() {
        // Pre-render to compile shaders before scroll starts
        viewer.setDirty()

        // Initialize Lenis smooth scroll only after model is ready
        initLenis()

        document.body.style.overflowY = "scroll"

        // Prevent color flash on initial scroll trigger setup
        let allowColorUpdates = false
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                allowColorUpdates = true
            })
        })

        const tl = gsap.timeline({ default: { ease: 'none' } })

        // FOREVER
        tl.to(position, {
            x: -1.83, y: -0.14, z: 6.15,
            scrollTrigger: { trigger: ".cam-view-2", start: "top bottom", end: "top top", scrub: true, immediateRender: false },
            onUpdate
        })

            .to(target, {
                x: isMobile ? 0 : -0.78, y: isMobile ? 1.5 : -0.03, z: -0.12,
                scrollTrigger: { trigger: ".cam-view-2", start: "top bottom", end: "top top", scrub: true, immediateRender: false },
                onUpdate
            })
            .to(ring.rotation, {
                x: 0, y: Math.PI, z: Math.PI / 2,
                scrollTrigger: { trigger: ".cam-view-2", start: "top bottom", end: "top top", scrub: true, immediateRender: false },
                onUpdate
            })
            .fromTo(colorLerpValue, { x: 0 }, {
                x: 1,
                scrollTrigger: { trigger: ".cam-view-2", start: "top bottom", end: "top top", scrub: true, immediateRender: false },
                onUpdate: function () {
                    if (!allowColorUpdates) return
                    silver.material.color.lerpColors(cachedGoldStart, cachedWhite, colorLerpValue.x)
                    gold.material.color.lerpColors(cachedGoldStart, cachedWhite, colorLerpValue.x)
                    for (const o of diamondObjects) {
                        o.material.color.lerpColors(cachedRed, cachedAqua, colorLerpValue.x)
                    }
                    onUpdate()
                }
            })

            .to('.hero--container', {
                opacity: 0, ease: "power4.out",
                scrollTrigger: { trigger: ".cam-view-2", start: "top bottom", end: "top top", scrub: true, immediateRender: false }
            })

            .to('.scroll-prompt', {
                opacity: 0, ease: "power4.out",
                scrollTrigger: { trigger: ".cam-view-2", start: "top bottom", end: "top 80%", scrub: true, immediateRender: false }
            })

            .fromTo('.forever--container', { opacity: 0, x: '-110%' }, {
                opacity: 1, x: '0%', ease: "power4.inOut",
                scrollTrigger: { trigger: ".cam-view-2", start: "top bottom", end: 'top top', scrub: true, immediateRender: false }
            })
            .addLabel("Forever")
            .to('.side-bar .unique', { opacity: 0.5, scale: 1, ease: "power4.inOut", scrollTrigger: { trigger: ".cam-view-2", start: "top bottom", end: 'top top', scrub: true, immediateRender: false } })
            .to('.side-bar .forever', { opacity: 1, scale: 1.5, ease: "power4.inOut", scrollTrigger: { trigger: ".cam-view-2", start: "top bottom", end: 'top top', scrub: true, immediateRender: false } })


            // EMOTIONS SECTION
            .to(position, {
                x: -0.06, y: -1.15, z: 4.42,
                scrollTrigger: { trigger: ".cam-view-3", start: "top bottom", end: "top top", scrub: true, immediateRender: false },
                onUpdate
            })
            .to(target, {
                x: -0.01, y: 0.9, z: 0.07,
                scrollTrigger: { trigger: ".cam-view-3", start: "top bottom", end: "top top", scrub: true, immediateRender: false },
                onUpdate
            })
            .to(ring.rotation, {
                x: 0, y: Math.PI, z: -Math.PI / 2,
                scrollTrigger: { trigger: ".cam-view-3", start: "top bottom", end: "top top", scrub: true, immediateRender: false },
                onUpdate
            })
            .fromTo(colorLerpValue2, { x: 0 }, {
                x: 1,
                scrollTrigger: { trigger: ".cam-view-3", start: "top bottom", end: "top top", scrub: true, immediateRender: false },
                onUpdate: function () {
                    if (!allowColorUpdates) return
                    silver.material.color.lerpColors(cachedWhite, cachedWhite, colorLerpValue2.x)
                    gold.material.color.lerpColors(cachedWhite, cachedWhite, colorLerpValue2.x)
                    for (const o of diamondObjects) {
                        o.material.color.lerpColors(cachedAqua, cachedViolet, colorLerpValue2.x)
                    }
                    onUpdate()
                }
            })
            .to('.forever--container', {
                opacity: 0, x: '-110%', ease: "power4.inOut",
                scrollTrigger: { trigger: ".cam-view-3", start: "top bottom", end: 'top top', scrub: true, immediateRender: false }
            })
            .fromTo('.emotions--container', { opacity: 0 }, {
                opacity: 1, ease: "power4.inOut",
                scrollTrigger: { trigger: ".cam-view-3", start: "top bottom", end: "top top", scrub: true, immediateRender: false }
            })

            .addLabel("Emotions")
            .to('.side-bar .forever', { opacity: 0.5, scale: 1, ease: "power4.inOut", scrollTrigger: { trigger: ".cam-view-3", start: "top bottom", end: 'top top', scrub: true, immediateRender: false } })
            .to('.side-bar .emotions', { opacity: 1, scale: 1.5, ease: "power4.inOut", scrollTrigger: { trigger: ".cam-view-3", start: "top bottom", end: 'top top', scrub: true, immediateRender: false } })

    }

    let needsUpdate = true;
    function onUpdate() {
        needsUpdate = true;
    }

    viewer.addEventListener('preFrame', () => {
        if (needsUpdate) {
            camera.positionUpdated(false)
            camera.targetUpdated(true)
            needsUpdate = false;
        }
    })

    // NAVIGATION EVENT
    document.querySelector('.forever')?.addEventListener('click', () => {
        const element = document.querySelector('.cam-view-2')
        window.scrollTo({ top: element?.getBoundingClientRect().top, left: 0, behavior: 'smooth' })
    })

    // CUSTOMIZE EVENT - Open questionnaire
    document.querySelector('.btn-customize')?.addEventListener('click', () => {
        openQuestionnaire()
    })

    function openQuestionnaire() {
        const overlay = document.querySelector('.questionnaire-overlay') as HTMLElement
        const firstStep = document.querySelector('.questionnaire-step[data-step="1"]') as HTMLElement

        if (overlay && firstStep) {
            overlay.style.display = 'block'
            setTimeout(() => {
                overlay.classList.add('active')
                firstStep.classList.add('active')
            }, 10)
        }
    }

    let colorLerpValue = { x: 0 }
    let colorLerpValue2 = { x: 0 }
}

/////////////////////////////////////////////////////////////////////////
///// QUESTIONNAIRE LOGIC
function initQuestionnaire() {
    const overlay = document.querySelector('.questionnaire-overlay') as HTMLElement
    const steps = document.querySelectorAll('.questionnaire-step')
    const continueButtons = document.querySelectorAll('.btn-continue')
    const skipBackLinks = document.querySelectorAll('.skip-back')
    const closeButtons = document.querySelectorAll('.btn-close-questionnaire')

    // Ring configuration state
    const ringConfig: RingConfig = {
        metal: 'Yellow Gold',
        gemstone: 'Diamond',
        cut: 'Round',
        setting: 'Solitaire',
        bandStyle: 'Plain',
        imageSize: '1K'
    }

    // Map stone carousel keys to gemstone names
    const stoneToGemstone: { [key: string]: RingConfig['gemstone'] } = {
        'diamond': 'Diamond',
        'emerald': 'Emerald',
        'citrine': 'Citrine',
        'sapphire': 'Sapphire',
        'ruby': 'Ruby',
        'amethyst': 'Amethyst',
        'aquamarine': 'Aquamarine',
        'ametrine': 'Ametrine',
        'tanzanite': 'Tanzanite'
    }

    // Map cut carousel keys to cut names
    const cutToCut: { [key: string]: RingConfig['cut'] } = {
        'round': 'Round',
        'princess': 'Princess',
        'oval': 'Oval',
        'emerald': 'Emerald',
        'pear': 'Pear',
        'marquise': 'Marquise',
        'cushion': 'Cushion',
        'ascher': 'Ascher',
        'baguette': 'Baguette',
        'trillion': 'Trillion'
    }

    // Header "Create your ring" button - Opens questionnaire
    document.querySelector('.btn-create-ring')?.addEventListener('click', (e) => {
        e.preventDefault()
        openQuestionnaireFromHeader()
    })

    function openQuestionnaireFromHeader() {
        const firstStep = document.querySelector('.questionnaire-step[data-step="1"]') as HTMLElement

        if (overlay && firstStep) {
            overlay.style.display = 'block'
            setTimeout(() => {
                overlay.classList.add('active')
                firstStep.classList.add('active')
            }, 10)
        }
    }

    // Initialize sliders with value capture
    const sliderTracks = document.querySelectorAll('.slider-track')
    sliderTracks.forEach(track => {
        const thumb = track.querySelector('.slider-thumb') as HTMLElement
        const sliderRow = track.closest('.slider-row') as HTMLElement
        const sliderId = sliderRow?.getAttribute('data-slider') as keyof SliderValues
        let isDragging = false

        const updateThumbPosition = (clientX: number) => {
            const rect = track.getBoundingClientRect()
            let percentage = (clientX - rect.left) / rect.width
            percentage = Math.max(0, Math.min(1, percentage))
            thumb.style.left = `${percentage * 100}%`

            // Store slider value: convert 0-1 to -1 to +1
            // Left (0%) = -1, Center (50%) = 0, Right (100%) = +1
            if (sliderId && sliderId in sliderValues) {
                sliderValues[sliderId] = (percentage * 2) - 1
            }
        }

        thumb.addEventListener('mousedown', () => {
            isDragging = true
            document.body.style.cursor = 'grabbing'
        })

        document.addEventListener('mousemove', (e) => {
            if (isDragging) {
                updateThumbPosition(e.clientX)
            }
        })

        document.addEventListener('mouseup', () => {
            isDragging = false
            document.body.style.cursor = ''
        })

        // Touch support
        thumb.addEventListener('touchstart', () => {
            isDragging = true
        })

        document.addEventListener('touchmove', (e) => {
            if (isDragging && e.touches[0]) {
                updateThumbPosition(e.touches[0].clientX)
            }
        })

        document.addEventListener('touchend', () => {
            isDragging = false
        })

        // Click on track to move thumb
        track.addEventListener('click', (e) => {
            updateThumbPosition((e as MouseEvent).clientX)
        })
    })

    // Stone carousel logic
    const stoneData: { [key: string]: { name: string; description: string; folder: string } } = {
        diamond: { name: 'DIAMOND', description: 'The ultimate symbol of brilliance and eternal love.', folder: 'diamond' },
        emerald: { name: 'EMERALD', description: 'A lush green gem representing renewal and wisdom.', folder: 'emerald' },
        citrine: { name: 'CITRINE', description: 'A warm golden stone radiating joy and abundance.', folder: 'citrine' },
        sapphire: { name: 'SAPPHIRE', description: 'A majestic blue gem symbolizing loyalty and truth.', folder: 'sapphire' },
        ametrine: { name: 'AMETRINE', description: 'A rare blend of amethyst and citrine in one stone.', folder: 'l_amertine' },
        tanzanite: { name: 'TANZANITE BICOLOR', description: 'An exotic gem with shifting violet and blue hues.', folder: 'tanzanite bicolor' },
        amethyst: { name: 'AMETHYST', description: 'A regal purple stone associated with clarity and calm.', folder: 'amethyst' },
        aquamarine: { name: 'AQUAMARINE', description: 'A serene blue gem evoking the tranquil sea.', folder: 'aquamarine' },
        ruby: { name: 'RUBY', description: 'A fiery red gem representing passion and vitality.', folder: 'ruby' }
    }

    // Cut data with descriptions
    const cutData: { [key: string]: { name: string; description: string } } = {
        ascher: { name: 'ASCHER', description: 'A square step-cut with cropped corners.' },
        baguette: { name: 'BAGUETTE', description: 'A rectangular step-cut with clean lines.' },
        cushion: { name: 'CUSHION', description: 'A soft square with rounded corners.' },
        emerald: { name: 'EMERALD', description: 'A rectangular step-cut with elegance.' },
        marquise: { name: 'MARQUISE', description: 'An elongated shape with pointed ends.' },
        oval: { name: 'OVAL', description: 'An elliptical shape with brilliant facets.' },
        pear: { name: 'PEAR', description: 'A teardrop shape combining round and marquise.' },
        princess: { name: 'PRINCESS', description: 'A square brilliant-cut with sharp corners.' },
        round: { name: 'ROUND', description: 'A classic brilliant cut with maximum sparkle.' },
        trillion: { name: 'TRILLION', description: 'A triangular shape with dramatic brilliance.' }
    }

    // Cuts available per stone (amethyst doesn't have oval)
    const stoneCuts: { [key: string]: string[] } = {
        diamond: ['round', 'princess', 'cushion', 'oval', 'emerald', 'pear', 'marquise', 'ascher', 'trillion', 'baguette'],
        emerald: ['round', 'princess', 'cushion', 'oval', 'emerald', 'pear', 'marquise', 'ascher', 'trillion', 'baguette'],
        citrine: ['round', 'princess', 'cushion', 'oval', 'emerald', 'pear', 'marquise', 'ascher', 'trillion', 'baguette'],
        sapphire: ['round', 'princess', 'cushion', 'oval', 'emerald', 'pear', 'marquise', 'ascher', 'trillion', 'baguette'],
        ametrine: ['round', 'princess', 'cushion', 'oval', 'emerald', 'pear', 'marquise', 'ascher', 'trillion', 'baguette'],
        tanzanite: ['round', 'princess', 'cushion', 'oval', 'emerald', 'pear', 'marquise', 'ascher', 'trillion', 'baguette'],
        amethyst: ['round', 'princess', 'cushion', 'emerald', 'pear', 'marquise', 'ascher', 'trillion', 'baguette'],
        aquamarine: ['round', 'princess', 'cushion', 'oval', 'emerald', 'pear', 'marquise', 'ascher', 'trillion', 'baguette'],
        ruby: ['round', 'princess', 'cushion', 'oval', 'emerald', 'pear', 'marquise', 'ascher', 'trillion', 'baguette']
    }

    let selectedStone = 'citrine'

    // Stone carousel - scoped to gems-carousel
    const gemsCarousel = document.querySelector('.gems-carousel') as HTMLElement
    const carouselTrack = gemsCarousel?.querySelector('.carousel-track') as HTMLElement
    const slides = gemsCarousel?.querySelectorAll('.carousel-slide') || []
    const prevBtn = gemsCarousel?.querySelector('.carousel-prev') as HTMLElement
    const nextBtn = gemsCarousel?.querySelector('.carousel-next') as HTMLElement
    const stoneName = document.querySelector('.stone-name') as HTMLElement
    const stoneDescription = document.querySelector('.stone-description') as HTMLElement

    let currentSlide = 0 // Start at diamond
    const totalSlides = slides.length

    function updateCarousel() {
        if (!carouselTrack || slides.length === 0) return

        // Calculate offset to center the current slide
        // Each slide is 50% width, we want to show current slide in center
        const slideWidth = 50
        const offset = -(currentSlide * slideWidth) + 25

        carouselTrack.style.transform = `translateX(${offset}%)`

        // Update active state
        slides.forEach((slide, index) => {
            slide.classList.toggle('active', index === currentSlide)
        })

        // Update stone info
        const currentStoneKey = slides[currentSlide]?.getAttribute('data-stone') || 'citrine'
        const stone = stoneData[currentStoneKey]
        if (stone && stoneName && stoneDescription) {
            stoneName.textContent = stone.name
            stoneDescription.textContent = stone.description
        }

        // Track selected stone for cut selection
        selectedStone = currentStoneKey
    }

    function goToNextSlide() {
        currentSlide = (currentSlide + 1) % totalSlides
        updateCarousel()
    }

    function goToPrevSlide() {
        currentSlide = (currentSlide - 1 + totalSlides) % totalSlides
        updateCarousel()
    }

    if (prevBtn) {
        prevBtn.addEventListener('click', goToPrevSlide)
    }

    if (nextBtn) {
        nextBtn.addEventListener('click', goToNextSlide)
    }

    // Initialize carousel
    updateCarousel()

    // Cut carousel logic
    const cutCarousel = document.querySelector('.cut-carousel') as HTMLElement
    const cutCarouselTrack = cutCarousel?.querySelector('.carousel-track') as HTMLElement
    const cutPrevBtn = cutCarousel?.querySelector('.carousel-prev') as HTMLElement
    const cutNextBtn = cutCarousel?.querySelector('.carousel-next') as HTMLElement
    const cutName = document.querySelector('.cut-name') as HTMLElement
    const cutDescription = document.querySelector('.cut-description') as HTMLElement

    let currentCutSlide = 0
    let currentCuts: string[] = []

    function populateCutCarousel() {
        if (!cutCarouselTrack) return

        currentCuts = stoneCuts[selectedStone] || []
        currentCutSlide = 0

        // Clear existing slides
        cutCarouselTrack.innerHTML = ''

        // Create slides for each cut using imported images
        currentCuts.forEach(cut => {
            const imgSrc = cutImages[selectedStone]?.[cut]
            if (!imgSrc) return

            const slide = document.createElement('div')
            slide.className = 'carousel-slide'
            slide.setAttribute('data-cut', cut)
            slide.innerHTML = `<img src="${imgSrc}" alt="${cutData[cut]?.name || cut}">`
            cutCarouselTrack.appendChild(slide)
        })

        updateCutCarousel()
    }

    function updateCutCarousel() {
        if (!cutCarouselTrack || currentCuts.length === 0) return

        const cutSlides = cutCarouselTrack.querySelectorAll('.carousel-slide')

        // Calculate offset to center the current slide
        const slideWidth = 50
        const offset = -(currentCutSlide * slideWidth) + 25

        cutCarouselTrack.style.transform = `translateX(${offset}%)`

        // Update active state
        cutSlides.forEach((slide, index) => {
            slide.classList.toggle('active', index === currentCutSlide)
        })

        // Update cut info
        const currentCutKey = currentCuts[currentCutSlide]
        const cut = cutData[currentCutKey]
        if (cut && cutName && cutDescription) {
            cutName.textContent = cut.name
            cutDescription.textContent = cut.description
        }
    }

    function goToNextCut() {
        currentCutSlide = (currentCutSlide + 1) % currentCuts.length
        updateCutCarousel()
    }

    function goToPrevCut() {
        currentCutSlide = (currentCutSlide - 1 + currentCuts.length) % currentCuts.length
        updateCutCarousel()
    }

    if (cutPrevBtn) {
        cutPrevBtn.addEventListener('click', goToPrevCut)
    }

    if (cutNextBtn) {
        cutNextBtn.addEventListener('click', goToNextCut)
    }

    // Metal carousel logic - scoped to step 3
    const metalData: { [key: string]: { name: string; description: string; configValue: RingConfig['metal'] } } = {
        gold: { name: 'GOLD', description: 'Classic warm-toned precious metal.', configValue: 'Yellow Gold' },
        silver: { name: 'SILVER', description: 'Cool-toned elegant precious metal.', configValue: 'Silver' }
    }

    const metalStep = document.querySelector('.questionnaire-step[data-step="3"]') as HTMLElement
    const metalCarousel = metalStep?.querySelector('.stone-carousel') as HTMLElement
    const metalCarouselTrack = metalCarousel?.querySelector('.carousel-track') as HTMLElement
    const metalSlides = metalCarousel?.querySelectorAll('.carousel-slide') || []
    const metalPrevBtn = metalCarousel?.querySelector('.carousel-prev') as HTMLElement
    const metalNextBtn = metalCarousel?.querySelector('.carousel-next') as HTMLElement
    const metalName = document.querySelector('.metal-name') as HTMLElement
    const metalDescription = document.querySelector('.metal-description') as HTMLElement

    let currentMetalSlide = 0
    const totalMetalSlides = metalSlides.length

    function updateMetalCarousel() {
        if (!metalCarouselTrack || metalSlides.length === 0) return

        const slideWidth = 50
        const offset = -(currentMetalSlide * slideWidth) + 25

        metalCarouselTrack.style.transform = `translateX(${offset}%)`

        metalSlides.forEach((slide, index) => {
            slide.classList.toggle('active', index === currentMetalSlide)
        })

        const currentMetalKey = metalSlides[currentMetalSlide]?.getAttribute('data-metal') || 'gold'
        const metal = metalData[currentMetalKey]
        if (metal && metalName && metalDescription) {
            metalName.textContent = metal.name
            metalDescription.textContent = metal.description
        }

        // Update ringConfig
        if (metal) {
            ringConfig.metal = metal.configValue
        }
    }

    function goToNextMetal() {
        currentMetalSlide = (currentMetalSlide + 1) % totalMetalSlides
        updateMetalCarousel()
    }

    function goToPrevMetal() {
        currentMetalSlide = (currentMetalSlide - 1 + totalMetalSlides) % totalMetalSlides
        updateMetalCarousel()
    }

    if (metalPrevBtn) {
        metalPrevBtn.addEventListener('click', goToPrevMetal)
    }

    if (metalNextBtn) {
        metalNextBtn.addEventListener('click', goToNextMetal)
    }

    // Initialize metal carousel
    updateMetalCarousel()

    // Setting carousel logic
    const settingData: { [key: string]: { name: string; description: string } } = {
        'Solitaire': { name: 'SOLITAIRE', description: 'A classic single stone setting.' },
        'Pave': { name: 'PAVE', description: 'Band encrusted with small accent stones.' },
        'Three Stone': { name: 'THREE STONE', description: 'Three stones representing past, present, future.' },
        '4 Prongs': { name: '4 PRONGS', description: 'Gemstone secured by four subtle corner claws.' },
        'Signet': { name: 'SIGNET', description: 'Large flat oval surface with central cabochon stone.' },
        'Double Four Prongs': { name: 'DOUBLE FOUR PRONGS', description: 'Gemstone held by four elegant double-prongs.' },
        'Bezel': { name: 'BEZEL', description: 'Stone surrounded by a metal rim for protection.' },
        'Crown': { name: 'CROWN', description: 'Row of claw-like prongs securing the stone.' },
        'Lozenge Pave': { name: 'LOZENGE PAVE', description: 'Diamond-shaped bezel with clustered stones.' },
        'Chunky Oval': { name: 'CHUNKY OVAL', description: 'Bold thick oval shape paved with stones.' },
        'Pseudo Claws': { name: 'PSEUDO CLAWS', description: 'Dense row of sharp prongs around the stone.' },
        'Pave Marquise': { name: 'PAVE MARQUISE', description: 'Marquise shape with polished gold border.' },
        'Plain Marquise': { name: 'PLAIN MARQUISE', description: 'Marquise shape with polished border.' }
    }

    const settingCarousel = document.querySelector('.setting-carousel') as HTMLElement
    const settingCarouselTrack = settingCarousel?.querySelector('.carousel-track') as HTMLElement
    const settingSlides = settingCarousel?.querySelectorAll('.carousel-slide') || []
    const settingPrevBtn = settingCarousel?.querySelector('.carousel-prev') as HTMLElement
    const settingNextBtn = settingCarousel?.querySelector('.carousel-next') as HTMLElement
    const settingName = document.querySelector('.setting-name') as HTMLElement
    const settingDescription = document.querySelector('.setting-description') as HTMLElement

    let currentSettingSlide = 0
    const totalSettingSlides = settingSlides.length

    function updateSettingCarousel() {
        if (!settingCarouselTrack || settingSlides.length === 0) return

        const slideWidth = 50
        const offset = -(currentSettingSlide * slideWidth) + 25

        settingCarouselTrack.style.transform = `translateX(${offset}%)`

        settingSlides.forEach((slide, index) => {
            slide.classList.toggle('active', index === currentSettingSlide)
        })

        const currentSettingKey = settingSlides[currentSettingSlide]?.getAttribute('data-setting') || 'Solitaire'
        const setting = settingData[currentSettingKey]
        if (setting && settingName && settingDescription) {
            settingName.textContent = setting.name
            settingDescription.textContent = setting.description
        }

        // Update ringConfig
        ringConfig.setting = currentSettingKey as RingConfig['setting']
    }

    function goToNextSetting() {
        currentSettingSlide = (currentSettingSlide + 1) % totalSettingSlides
        updateSettingCarousel()
    }

    function goToPrevSetting() {
        currentSettingSlide = (currentSettingSlide - 1 + totalSettingSlides) % totalSettingSlides
        updateSettingCarousel()
    }

    if (settingPrevBtn) {
        settingPrevBtn.addEventListener('click', goToPrevSetting)
    }

    if (settingNextBtn) {
        settingNextBtn.addEventListener('click', goToNextSetting)
    }

    // Initialize setting carousel
    updateSettingCarousel()

    // Band carousel logic
    const bandData: { [key: string]: { name: string; description: string; file: string } } = {
        'Plain': { name: 'PLAIN', description: 'A classic smooth band.', file: 'plain' },
        'Split Shank': { name: 'SPLIT SHANK', description: 'Band splits into two as it approaches the setting.', file: 'split shank' },
        'Bypass': { name: 'BYPASS', description: 'An asymmetrical band with shoulders sweeping from opposite directions.', file: 'bypass' },
        'Euro Shank': { name: 'EURO SHANK', description: 'A squared-off band for comfort and stability.', file: 'euro style' },
        'Tapered': { name: 'TAPERED', description: 'A sculptural ribbed band with vertical grooves.', file: 'tapered' },
        'Twisted': { name: 'TWISTED', description: 'A sophisticated helical twisted cable texture.', file: 'twisted' },
        'Trefoil': { name: 'TREFOIL', description: 'Sheet metal with scalloped edges and hammered cupped sections.', file: 'trefoil' },
        'Checker': { name: 'CHECKER', description: 'Square settings in an alternating zig-zag pattern.', file: 'checker' },
        'Checkerboard': { name: 'CHECKERBOARD', description: 'Two-row checkerboard pattern with alternating stones.', file: 'checker board' },
        'Coiled': { name: 'COILED', description: 'Smooth band with coils around the shoulders.', file: 'coiled' },
        'Studded': { name: 'STUDDED', description: 'Band with flat round studs and smaller beads.', file: 'studded' },
        'Lattice Pave': { name: 'LATTICE PAVE', description: 'Openwork geometric lattice with accent stones.', file: 'lattice pave' },
        'Spiral': { name: 'SPIRAL', description: 'Multi-coil band with dangling disc charms.', file: 'spiral' },
        'Smooth Fluted': { name: 'SMOOTH FLUTED', description: 'Polished band with classical parallel fluting.', file: 'smooth fluted' },
        'Corrugated': { name: 'CORRUGATED', description: 'Evenly spaced raised vertical ridges.', file: 'corrugated' },
        'Open Cuff': { name: 'OPEN CUFF', description: 'Bold open-front design with a prominent gap.', file: 'open cuff' },
        'Signet': { name: 'SIGNET', description: 'Widens into a flat rectangular block across the top.', file: 'signet' },
        'Contrast Asymmetry': { name: 'CONTRAST ASYMMETRY', description: 'Two-tone split with scattered accent stones.', file: 'contrast asymmetry' }
    }

    const bandStyles = ['Plain', 'Split Shank', 'Bypass', 'Euro Shank', 'Tapered', 'Twisted', 'Trefoil', 'Checker', 'Checkerboard', 'Coiled', 'Studded', 'Lattice Pave', 'Spiral', 'Smooth Fluted', 'Corrugated', 'Open Cuff', 'Signet', 'Contrast Asymmetry']

    const bandCarousel = document.querySelector('.band-carousel') as HTMLElement
    const bandCarouselTrack = bandCarousel?.querySelector('.carousel-track') as HTMLElement
    const bandPrevBtn = bandCarousel?.querySelector('.carousel-prev') as HTMLElement
    const bandNextBtn = bandCarousel?.querySelector('.carousel-next') as HTMLElement
    const bandName = document.querySelector('.band-name') as HTMLElement
    const bandDescription = document.querySelector('.band-description') as HTMLElement

    let currentBandSlide = 0
    let selectedMetal = 'gold'

    function getSelectedMetal(): string {
        const currentMetalKey = metalSlides[currentMetalSlide]?.getAttribute('data-metal') || 'gold'
        return currentMetalKey
    }

    function populateBandCarousel() {
        if (!bandCarouselTrack) return

        selectedMetal = getSelectedMetal()
        currentBandSlide = 0

        // Clear existing slides
        bandCarouselTrack.innerHTML = ''

        // Create slides for each band style using imported images
        bandStyles.forEach(style => {
            const band = bandData[style]
            const imgSrc = bandImages[selectedMetal]?.[style]
            if (!band || !imgSrc) return

            const slide = document.createElement('div')
            slide.className = 'carousel-slide'
            slide.setAttribute('data-band', style)
            slide.innerHTML = `<img src="${imgSrc}" alt="${band.name}">`
            bandCarouselTrack.appendChild(slide)
        })

        updateBandCarousel()
    }

    function updateBandCarousel() {
        if (!bandCarouselTrack) return

        const bandSlides = bandCarouselTrack.querySelectorAll('.carousel-slide')
        if (bandSlides.length === 0) return

        const slideWidth = 50
        const offset = -(currentBandSlide * slideWidth) + 25

        bandCarouselTrack.style.transform = `translateX(${offset}%)`

        bandSlides.forEach((slide, index) => {
            slide.classList.toggle('active', index === currentBandSlide)
        })

        const currentBandKey = bandSlides[currentBandSlide]?.getAttribute('data-band') || 'Plain'
        const band = bandData[currentBandKey]
        if (band && bandName && bandDescription) {
            bandName.textContent = band.name
            bandDescription.textContent = band.description
        }

        // Update ringConfig
        ringConfig.bandStyle = currentBandKey as RingConfig['bandStyle']
    }

    function goToNextBand() {
        const bandSlides = bandCarouselTrack?.querySelectorAll('.carousel-slide') || []
        currentBandSlide = (currentBandSlide + 1) % bandSlides.length
        updateBandCarousel()
    }

    function goToPrevBand() {
        const bandSlides = bandCarouselTrack?.querySelectorAll('.carousel-slide') || []
        currentBandSlide = (currentBandSlide - 1 + bandSlides.length) % bandSlides.length
        updateBandCarousel()
    }

    if (bandPrevBtn) {
        bandPrevBtn.addEventListener('click', goToPrevBand)
    }

    if (bandNextBtn) {
        bandNextBtn.addEventListener('click', goToNextBand)
    }

    // Option pills initialization (Band Style - kept for backward compatibility but not used)
    const optionGrids = document.querySelectorAll('.option-grid')
    optionGrids.forEach(grid => {
        const optionType = grid.getAttribute('data-option-type') as 'bandStyle'
        const pills = grid.querySelectorAll('.option-pill')

        // Set initial active state
        pills.forEach(pill => {
            const value = pill.getAttribute('data-value')
            if (value === ringConfig[optionType]) {
                pill.classList.add('active')
            }
        })

        pills.forEach(pill => {
            pill.addEventListener('click', () => {
                // Remove active from all pills in this grid
                pills.forEach(p => p.classList.remove('active'))
                // Add active to clicked pill
                pill.classList.add('active')
                // Update config
                const value = pill.getAttribute('data-value')
                if (value && optionType) {
                    (ringConfig as any)[optionType] = value
                }
            })
        })
    })

    // Size selector initialization
    const sizePills = document.querySelectorAll('.size-pill')
    sizePills.forEach(pill => {
        pill.addEventListener('click', () => {
            sizePills.forEach(p => p.classList.remove('active'))
            pill.classList.add('active')
            const size = pill.getAttribute('data-size') as ImageSize
            if (size) {
                ringConfig.imageSize = size
            }
        })
    })

    // Generate button handler
    const generateBtn = document.querySelector('.btn-generate') as HTMLButtonElement
    const previewPlaceholder = document.querySelector('.preview-placeholder') as HTMLElement
    const previewImage = document.querySelector('.preview-image') as HTMLImageElement
    const generatePreview = document.querySelector('.generate-preview') as HTMLElement

    if (generateBtn) {
        generateBtn.addEventListener('click', async () => {
            // Update config from current selections
            updateConfigFromSelections()

            // Show loading state
            generateBtn.disabled = true
            generateBtn.classList.add('loading')
            generateBtn.innerHTML = 'Generating...'

            if (previewPlaceholder) {
                previewPlaceholder.innerHTML = `
                    <div class="preview-loading">
                        <div class="spinner"></div>
                        <p>Crafting your design...</p>
                    </div>
                `
            }

            try {
                const imageUrl = await generateRingImage(ringConfig)

                if (previewPlaceholder) {
                    previewPlaceholder.style.display = 'none'
                }
                if (previewImage) {
                    previewImage.src = imageUrl
                    previewImage.style.display = 'block'
                }

                // Add download button if not exists
                if (generatePreview && !generatePreview.querySelector('.btn-download')) {
                    const downloadBtn = document.createElement('a')
                    downloadBtn.className = 'btn-download'
                    downloadBtn.href = imageUrl
                    downloadBtn.download = 'bespoke-ring.png'
                    downloadBtn.innerHTML = `
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="7 10 12 15 17 10"></polyline>
                            <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                    `
                    generatePreview.appendChild(downloadBtn)
                }
            } catch (err: any) {
                console.error('Generation failed:', err)
                if (previewPlaceholder) {
                    previewPlaceholder.innerHTML = `
                        <div class="preview-error">
                            <p><strong>Generation Failed</strong></p>
                            <p>${err.message || 'Failed to generate image'}</p>
                            <button class="btn-dismiss">Dismiss</button>
                        </div>
                    `
                    const dismissBtn = previewPlaceholder.querySelector('.btn-dismiss')
                    dismissBtn?.addEventListener('click', () => {
                        previewPlaceholder.innerHTML = `
                            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" opacity="0.3">
                                <path d="M12 3l1.912 5.813L20 10l-4.764 3.063L17 19l-5-3.5L7 19l1.764-5.937L4 10l6.088-1.187z"/>
                            </svg>
                            <p>Your masterpiece awaits</p>
                        `
                    })
                }
            } finally {
                // Reset button state
                generateBtn.disabled = false
                generateBtn.classList.remove('loading')
                generateBtn.innerHTML = `
                    <svg class="sparkle-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M12 3l1.912 5.813L20 10l-4.764 3.063L17 19l-5-3.5L7 19l1.764-5.937L4 10l6.088-1.187z"/>
                    </svg>
                    Generate Preview
                `
            }
        })
    }

    // Update ringConfig from current carousel and pill selections
    // Only used for the full (non-personality) flow
    function updateConfigFromSelections() {
        // Skip if using personality flow - those carousels already updated ringConfig
        if (usingPersonalityFlow) {
            return
        }

        // Get stone from carousel (full flow step 4)
        const currentStoneKey = slides[currentSlide]?.getAttribute('data-stone') || 'diamond'
        ringConfig.gemstone = stoneToGemstone[currentStoneKey] || 'Diamond'

        // Get cut from cut carousel (full flow step 5)
        const currentCutKey = currentCuts[currentCutSlide] || 'round'
        ringConfig.cut = cutToCut[currentCutKey] || 'Round'
    }

    // Update summary display
    function updateSummary() {
        updateConfigFromSelections()

        const stoneSummary = document.querySelector('[data-summary="stone"]')
        const cutSummary = document.querySelector('[data-summary="cut"]')
        const metalSummary = document.querySelector('[data-summary="metal"]')
        const settingSummary = document.querySelector('[data-summary="setting"]')
        const bandStyleSummary = document.querySelector('[data-summary="bandStyle"]')

        if (stoneSummary) stoneSummary.textContent = ringConfig.gemstone
        if (cutSummary) cutSummary.textContent = ringConfig.cut
        if (metalSummary) metalSummary.textContent = ringConfig.metal
        if (settingSummary) settingSummary.textContent = ringConfig.setting
        if (bandStyleSummary) bandStyleSummary.textContent = ringConfig.bandStyle
    }

    // Step flow order (including personality steps)
    // Personality flow: 1 -> 2 -> 2.5 -> 2.6 (material) -> 2.7 (setting) -> 2.8 (band) -> 2.9 (stone) -> 2.10 (cut) -> 8
    // Skip-all flow: 1 -> 2 -> 2.5 -> 3 -> 4 -> 5 -> 6 -> 7 -> 8
    const personalityStepOrder = ['1', '2', '2.5', '2.6', '2.7', '2.8', '2.9', '2.10', '8']
    const fullStepOrder = ['1', '2', '2.5', '3', '4', '5', '6', '7', '8']
    let stepOrder = personalityStepOrder
    let currentStepIndex = 0
    let usingPersonalityFlow = true

    // Helper to get current step ID
    function getCurrentStepId(): string {
        return stepOrder[currentStepIndex]
    }

    // Helper to get next step ID
    function getNextStepId(): string {
        if (currentStepIndex < stepOrder.length - 1) {
            return stepOrder[currentStepIndex + 1]
        }
        return stepOrder[currentStepIndex]
    }

    // Helper to get previous step ID
    function getPreviousStepId(): string {
        if (currentStepIndex > 0) {
            return stepOrder[currentStepIndex - 1]
        }
        return stepOrder[0]
    }

    // Continue button handlers
    continueButtons.forEach(btn => {
        btn.addEventListener('click', () => {
            const currentStepId = getCurrentStepId()
            const nextStepId = getNextStepId()

            if (currentStepIndex < stepOrder.length - 1) {
                // If finishing personality step 2, compute scores and generate recommendations
                if (currentStepId === '2') {
                    personalityScores = computePersonalityScores()
                    recommendations = generateRecommendations(personalityScores)
                    console.log('Personality scores:', personalityScores)
                    console.log('Recommendations:', recommendations)
                }

                // If entering personality material step, set up carousel and info
                if (nextStepId === '2.6') {
                    setupPersonalityMaterialCarousel()
                }

                // If entering personality setting step
                if (nextStepId === '2.7') {
                    setupPersonalitySettingCarousel()
                }

                // If entering personality band step
                if (nextStepId === '2.8') {
                    setupPersonalityBandCarousel()
                }

                // If entering personality stone step
                if (nextStepId === '2.9') {
                    setupPersonalityStoneCarousel()
                }

                // If entering personality cut step
                if (nextStepId === '2.10') {
                    setupPersonalityCutCarousel()
                }

                // If moving from stone selection (step 4) to cut selection (step 5), populate cut carousel
                if (currentStepId === '4') {
                    populateCutCarousel()
                }

                // If moving from cut selection (step 5) to band selection (step 6), populate band carousel
                if (currentStepId === '5') {
                    populateBandCarousel()
                }

                // If moving to generate step (step 8), update the summary
                if (currentStepId === '7' || currentStepId === '2.10') {
                    updateSummary()
                }

                // Go to next step
                const currentStepEl = document.querySelector(`.questionnaire-step[data-step="${currentStepId}"]`) as HTMLElement
                const nextStepEl = document.querySelector(`.questionnaire-step[data-step="${nextStepId}"]`) as HTMLElement

                if (currentStepEl && nextStepEl) {
                    currentStepEl.classList.remove('active')
                    setTimeout(() => {
                        currentStepEl.style.display = 'none'
                        nextStepEl.style.display = 'flex'
                        setTimeout(() => {
                            nextStepEl.classList.add('active')
                        }, 10)
                    }, 400)
                    currentStepIndex++
                }
            } else {
                // Complete questionnaire - close overlay
                closeQuestionnaire()
            }
        })
    })

    // Close button handlers
    closeButtons.forEach(btn => {
        btn.addEventListener('click', () => {
            closeQuestionnaireOnly()
        })
    })

    // Skip to all options button handler
    const skipAllBtn = document.querySelector('.btn-skip-all')
    if (skipAllBtn) {
        skipAllBtn.addEventListener('click', () => {
            // Switch to full step order (skipping personality match steps)
            usingPersonalityFlow = false
            stepOrder = fullStepOrder

            // Skip personality match steps and go directly to metal selection (step 3)
            const currentStepEl = document.querySelector(`.questionnaire-step[data-step="${getCurrentStepId()}"]`) as HTMLElement
            const metalStepEl = document.querySelector('.questionnaire-step[data-step="3"]') as HTMLElement

            if (currentStepEl && metalStepEl) {
                currentStepEl.classList.remove('active')
                setTimeout(() => {
                    currentStepEl.style.display = 'none'
                    metalStepEl.style.display = 'flex'
                    setTimeout(() => {
                        metalStepEl.classList.add('active')
                    }, 10)
                }, 400)
                // Update step index to step 3 in the full flow
                currentStepIndex = fullStepOrder.indexOf('3')
            }
        })
    }

    // Personality Material Carousel setup
    function setupPersonalityMaterialCarousel() {
        const carousel = document.querySelector('.personality-material-carousel') as HTMLElement
        const track = carousel?.querySelector('.carousel-track') as HTMLElement
        const slides = carousel?.querySelectorAll('.carousel-slide') || []
        const prevBtn = carousel?.querySelector('.carousel-prev') as HTMLElement
        const nextBtn = carousel?.querySelector('.carousel-next') as HTMLElement
        const nameEl = document.querySelector('.personality-material-name') as HTMLElement
        const descEl = document.querySelector('.personality-material-description') as HTMLElement

        let currentIndex = recommendations.materialIndex

        function updateCarousel() {
            if (!track || slides.length === 0) return

            const slideWidth = 50
            const offset = -(currentIndex * slideWidth) + 25
            track.style.transform = `translateX(${offset}%)`

            slides.forEach((slide, index) => {
                slide.classList.toggle('active', index === currentIndex)
                slide.classList.toggle('recommended', index === recommendations.materialIndex)
            })

            const metalKey = slides[currentIndex]?.getAttribute('data-metal') || 'gold'
            const metalData: { [key: string]: { name: string; configValue: RingConfig['metal'] } } = {
                gold: { name: 'GOLD', configValue: 'Yellow Gold' },
                silver: { name: 'SILVER', configValue: 'Silver' }
            }

            if (nameEl) nameEl.textContent = metalData[metalKey]?.name || 'MATERIAL'
            if (descEl) descEl.textContent = getRecommendationDescription('material')

            // Update ring config
            ringConfig.metal = metalData[metalKey]?.configValue || 'Yellow Gold'
        }

        prevBtn?.addEventListener('click', () => {
            currentIndex = (currentIndex - 1 + slides.length) % slides.length
            updateCarousel()
        })

        nextBtn?.addEventListener('click', () => {
            currentIndex = (currentIndex + 1) % slides.length
            updateCarousel()
        })

        updateCarousel()
    }

    // Personality Setting Carousel setup
    function setupPersonalitySettingCarousel() {
        const carousel = document.querySelector('.personality-setting-carousel') as HTMLElement
        const track = carousel?.querySelector('.carousel-track') as HTMLElement
        const slides = carousel?.querySelectorAll('.carousel-slide') || []
        const prevBtn = carousel?.querySelector('.carousel-prev') as HTMLElement
        const nextBtn = carousel?.querySelector('.carousel-next') as HTMLElement
        const nameEl = document.querySelector('.personality-setting-name') as HTMLElement
        const descEl = document.querySelector('.personality-setting-description') as HTMLElement

        let currentIndex = recommendations.settingIndex

        function updateCarousel() {
            if (!track || slides.length === 0) return

            const slideWidth = 50
            const offset = -(currentIndex * slideWidth) + 25
            track.style.transform = `translateX(${offset}%)`

            slides.forEach((slide, index) => {
                slide.classList.toggle('active', index === currentIndex)
                slide.classList.toggle('recommended', index === recommendations.settingIndex)
            })

            const settingKey = slides[currentIndex]?.getAttribute('data-setting') || 'Solitaire'

            if (nameEl) nameEl.textContent = settingKey.toUpperCase()
            if (descEl) descEl.textContent = getRecommendationDescription('setting')

            // Update ring config
            ringConfig.setting = settingKey as RingConfig['setting']
        }

        prevBtn?.addEventListener('click', () => {
            currentIndex = (currentIndex - 1 + slides.length) % slides.length
            updateCarousel()
        })

        nextBtn?.addEventListener('click', () => {
            currentIndex = (currentIndex + 1) % slides.length
            updateCarousel()
        })

        updateCarousel()
    }

    // Personality Band Carousel setup
    function setupPersonalityBandCarousel() {
        const carousel = document.querySelector('.personality-band-carousel') as HTMLElement
        const track = carousel?.querySelector('.carousel-track') as HTMLElement
        const prevBtn = carousel?.querySelector('.carousel-prev') as HTMLElement
        const nextBtn = carousel?.querySelector('.carousel-next') as HTMLElement
        const nameEl = document.querySelector('.personality-band-name') as HTMLElement
        const descEl = document.querySelector('.personality-band-description') as HTMLElement

        // Get selected metal from personality material step
        const selectedMetal = ringConfig.metal === 'Yellow Gold' ? 'gold' : 'silver'

        let currentIndex = recommendations.bandIndex

        // Populate band slides
        if (track) {
            track.innerHTML = ''
            allBandStyles.forEach((style, index) => {
                const imgSrc = bandImages[selectedMetal]?.[style]
                if (!imgSrc) return

                const slide = document.createElement('div')
                slide.className = 'carousel-slide'
                if (index === recommendations.bandIndex) {
                    slide.className += ' recommended'
                }
                slide.setAttribute('data-band', style)
                slide.innerHTML = `<img src="${imgSrc}" alt="${style}">`
                track.appendChild(slide)
            })
        }

        function updateCarousel() {
            const slides = track?.querySelectorAll('.carousel-slide') || []
            if (!track || slides.length === 0) return

            const slideWidth = 50
            const offset = -(currentIndex * slideWidth) + 25
            track.style.transform = `translateX(${offset}%)`

            slides.forEach((slide, index) => {
                slide.classList.toggle('active', index === currentIndex)
            })

            const bandKey = allBandStyles[currentIndex] || 'Plain'

            if (nameEl) nameEl.textContent = bandKey.toUpperCase()
            if (descEl) descEl.textContent = getRecommendationDescription('band')

            // Update ring config
            ringConfig.bandStyle = bandKey as RingConfig['bandStyle']
        }

        prevBtn?.addEventListener('click', () => {
            const slides = track?.querySelectorAll('.carousel-slide') || []
            currentIndex = (currentIndex - 1 + slides.length) % slides.length
            updateCarousel()
        })

        nextBtn?.addEventListener('click', () => {
            const slides = track?.querySelectorAll('.carousel-slide') || []
            currentIndex = (currentIndex + 1) % slides.length
            updateCarousel()
        })

        updateCarousel()
    }

    // Personality Stone Carousel setup
    let personalitySelectedStone = 'diamond'
    function setupPersonalityStoneCarousel() {
        const carousel = document.querySelector('.personality-stone-carousel') as HTMLElement
        const track = carousel?.querySelector('.carousel-track') as HTMLElement
        const slides = carousel?.querySelectorAll('.carousel-slide') || []
        const prevBtn = carousel?.querySelector('.carousel-prev') as HTMLElement
        const nextBtn = carousel?.querySelector('.carousel-next') as HTMLElement
        const nameEl = document.querySelector('.personality-stone-name') as HTMLElement
        const descEl = document.querySelector('.personality-stone-description') as HTMLElement

        let currentIndex = recommendations.stoneIndex

        function updateCarousel() {
            if (!track || slides.length === 0) return

            const slideWidth = 50
            const offset = -(currentIndex * slideWidth) + 25
            track.style.transform = `translateX(${offset}%)`

            slides.forEach((slide, index) => {
                slide.classList.toggle('active', index === currentIndex)
                slide.classList.toggle('recommended', index === recommendations.stoneIndex)
            })

            const stoneKey = slides[currentIndex]?.getAttribute('data-stone') || 'diamond'
            personalitySelectedStone = stoneKey
            const stoneNames: { [key: string]: string } = {
                'diamond': 'DIAMOND', 'amethyst': 'AMETHYST', 'aquamarine': 'AQUAMARINE',
                'ruby': 'RUBY', 'emerald': 'EMERALD', 'citrine': 'CITRINE',
                'sapphire': 'SAPPHIRE', 'ametrine': 'AMETRINE', 'tanzanite': 'TANZANITE BICOLOR'
            }

            if (nameEl) nameEl.textContent = stoneNames[stoneKey] || 'STONE'
            if (descEl) descEl.textContent = getRecommendationDescription('stone')

            // Update ring config
            ringConfig.gemstone = stoneToGemstone[stoneKey] || 'Diamond'
        }

        prevBtn?.addEventListener('click', () => {
            currentIndex = (currentIndex - 1 + slides.length) % slides.length
            updateCarousel()
        })

        nextBtn?.addEventListener('click', () => {
            currentIndex = (currentIndex + 1) % slides.length
            updateCarousel()
        })

        updateCarousel()
    }

    // Personality Cut Carousel setup
    function setupPersonalityCutCarousel() {
        const carousel = document.querySelector('.personality-cut-carousel') as HTMLElement
        const track = carousel?.querySelector('.carousel-track') as HTMLElement
        const prevBtn = carousel?.querySelector('.carousel-prev') as HTMLElement
        const nextBtn = carousel?.querySelector('.carousel-next') as HTMLElement
        const nameEl = document.querySelector('.personality-cut-name') as HTMLElement
        const descEl = document.querySelector('.personality-cut-description') as HTMLElement

        // Get cuts available for selected stone
        const availableCuts = stoneCuts[personalitySelectedStone] || stoneCuts['diamond']

        // Find recommended cut index
        const recommendedCutIndex = availableCuts.findIndex(cut =>
            recommendations.stoneCuts.includes(cut)
        )
        let currentIndex = recommendedCutIndex >= 0 ? recommendedCutIndex : 0

        // Populate cut slides
        if (track) {
            track.innerHTML = ''
            availableCuts.forEach((cut, index) => {
                const imgSrc = cutImages[personalitySelectedStone]?.[cut]
                if (!imgSrc) return

                const slide = document.createElement('div')
                slide.className = 'carousel-slide'
                if (recommendations.stoneCuts.includes(cut)) {
                    slide.className += ' recommended'
                }
                slide.setAttribute('data-cut', cut)
                slide.innerHTML = `<img src="${imgSrc}" alt="${cutData[cut]?.name || cut}">`
                track.appendChild(slide)
            })
        }

        function updateCarousel() {
            const slides = track?.querySelectorAll('.carousel-slide') || []
            if (!track || slides.length === 0) return

            const slideWidth = 50
            const offset = -(currentIndex * slideWidth) + 25
            track.style.transform = `translateX(${offset}%)`

            slides.forEach((slide, index) => {
                slide.classList.toggle('active', index === currentIndex)
            })

            const cutKey = availableCuts[currentIndex] || 'round'
            const cut = cutData[cutKey]

            if (nameEl) nameEl.textContent = cut?.name || 'CUT'
            if (descEl) descEl.textContent = getRecommendationDescription('cut')

            // Update ring config
            ringConfig.cut = cutToCut[cutKey] || 'Round'
        }

        prevBtn?.addEventListener('click', () => {
            const slides = track?.querySelectorAll('.carousel-slide') || []
            currentIndex = (currentIndex - 1 + slides.length) % slides.length
            updateCarousel()
        })

        nextBtn?.addEventListener('click', () => {
            const slides = track?.querySelectorAll('.carousel-slide') || []
            currentIndex = (currentIndex + 1) % slides.length
            updateCarousel()
        })

        updateCarousel()
    }

    // Skip/back handlers
    skipBackLinks.forEach(link => {
        link.addEventListener('click', (e) => {
            e.preventDefault()
            if (currentStepIndex > 0) {
                const currentStepId = getCurrentStepId()
                const prevStepId = getPreviousStepId()

                // Go back to previous step
                const currentStepEl = document.querySelector(`.questionnaire-step[data-step="${currentStepId}"]`) as HTMLElement
                const prevStepEl = document.querySelector(`.questionnaire-step[data-step="${prevStepId}"]`) as HTMLElement

                if (currentStepEl && prevStepEl) {
                    currentStepEl.classList.remove('active')
                    setTimeout(() => {
                        currentStepEl.style.display = 'none'
                        prevStepEl.style.display = 'flex'
                        setTimeout(() => {
                            prevStepEl.classList.add('active')
                        }, 10)
                    }, 400)
                    currentStepIndex--
                }
            } else {
                // Close questionnaire on first step
                closeQuestionnaireOnly()
            }
        })
    })

    function closeQuestionnaire() {
        overlay.classList.remove('active')
        setTimeout(() => {
            overlay.style.display = 'none'
            // Reset all steps
            steps.forEach((step) => {
                const stepEl = step as HTMLElement
                stepEl.classList.remove('active')
                // Only show step 1 by default
                const stepNum = stepEl.getAttribute('data-step')
                stepEl.style.display = stepNum === '1' ? 'flex' : 'none'
            })
            currentStepIndex = 0
            // Reset to personality flow for next time
            usingPersonalityFlow = true
            stepOrder = personalityStepOrder

            // Dispatch completion event
            window.dispatchEvent(new CustomEvent('questionnaireComplete'))
        }, 400)
    }

    function closeQuestionnaireOnly() {
        overlay.classList.remove('active')
        setTimeout(() => {
            overlay.style.display = 'none'
            // Reset all steps
            steps.forEach((step) => {
                const stepEl = step as HTMLElement
                stepEl.classList.remove('active')
                const stepNum = stepEl.getAttribute('data-step')
                stepEl.style.display = stepNum === '1' ? 'flex' : 'none'
            })
            currentStepIndex = 0
            // Reset to personality flow for next time
            usingPersonalityFlow = true
            stepOrder = personalityStepOrder
            // Scroll back to top to reset the website view
            window.scrollTo({ top: 0, behavior: 'smooth' })
        }, 400)
    }
}

// Initialize questionnaire when DOM is ready
document.addEventListener('DOMContentLoaded', initQuestionnaire)

setupViewer()
